import { useEffect, useState } from "react";
import { connect, mapProps, useForm } from "@formily/react";
import { IServicePartner } from "@rumble-it/common-lib/src/types/ServicePartner";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import { useStateMachine } from "little-state-machine";
import { Button, IRumbleFormScope } from ".";
import { IFormStep } from "@formily/antd-v5";
import { HeaderImageProps, ServicePartnerCard } from "./ServicePartner";
import { PlusOutlined, UpOutlined } from "@ant-design/icons";
import { Loading } from "../Loading";

interface ServicePartnerProps {
  // Form-related props
  value?: IServicePartner | null;
  onChange?: (servicePartner: IServicePartner) => void;
  errors?: Array<{ messages: string[] }>;

  // Component behavior props
  mode?: "single" | string;
  scrollTo?: boolean | string;
  fetchParams?: {
    [key: string]: any;
  };
  // HTML string that will be injected (ensure it's trusted!)
  contentOnInvalidServicepartner?: string;
  headerImage?: HeaderImageProps;
  preferredPartnerText?: string;
}

export const ServicePartnerSelector = (props: ServicePartnerProps) => {
  const form = useForm();
  const { state } = useStateMachine();

  const [servicePartners, setServicePartners] = useState<IServicePartner[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllServicePartners, setShowAllServicePartners] =
    useState<boolean>(false);
  const [selectedServicePartner, setSelectedServicePartner] =
    useState<IServicePartner | null>(props.value || null);

  // Safely extract scope and fetchData
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const fetchData = scope?.fetchData;
  if (!fetchData || typeof fetchData === "undefined") {
    throw new Error("fetchData function is not available");
  }
  const formStep = scope?.formStep as IFormStep;
  if (!formStep) {
    throw new Error("formStep function is not available");
  }
  const scrollTo = props.scrollTo ?? true;

  const globalField = form.query("global").take(); // TODO: Perhaps we should turn this into a useGlobalField hook for ourselves. But that depends on how we want to handle global state in the future.
  if (!globalField) {
    console.warn(
      "RumbleStepButtons: ",
      "Global field is not available. Please ensure you have a global field defined in your form schema.",
    );
  }

  const setButtonDisabled = (buttonName: string, disabled: boolean) => {
    if (!globalField) return; // Ensure globalField is available, otherwise skip setting state.

    globalField.setState((state: any) => {
      if (!state.data.buttonState) {
        state.data.buttonState = {};
      }

      if (!state.data.buttonState.disabled) {
        state.data.buttonState.disabled = {};
      }

      state.data.buttonState.disabled[buttonName] = disabled;
    });
  };

  const setNextButtonDisabled = (disabled: boolean) => {
    setButtonDisabled("next", disabled);
  };

  // Get values from form
  const postalCode = form.values.postalCode;
  const streetNumber = form.values.streetNumber;

  useEffect(() => {
    async function fetchServicePartners() {
      setLoading(true);
      setNextButtonDisabled(true);
      setError(null);

      const fetchParams = props.fetchParams || {};

      const params = {
        country: postalCode ? "NL" : null,
        postalCode: postalCode,
        streetNumber: streetNumber,
        ...fetchParams,
      };

      try {
        if (!fetchData) {
          throw new Error("fetchData function is not available");
        }
        const response = await fetchData({
          endpoint: "service-partners/selector",
          queryParameters: params,
          /*
           * This API method heavily relies on our Loco service.
           * In a normal situation, two queries take place:
           * - One for address -> geolocation.
           * - Another one for address -> travel distance and duration to all addresses.
           * The backend has a timeout of 10s for each call to Loco (so third-party API query time ~ 20s).
           * We set this timeout to 40s to be safe (as the default timeout is 30s).
           */
          timeout: 40000,
        });

        if (response && response.data) {
          setServicePartners(response.data);
        } else {
          throw new Error("Geen service partners gevonden");
        }
      } catch (err: any) {
        setError(err?.message || String(err));
      } finally {
        setLoading(false);
        setNextButtonDisabled(false);
      }
    }

    fetchServicePartners();
  }, [postalCode, streetNumber, fetchData]);

  const handleSelectServicePartner = (servicePartner: IServicePartner) => {
    form.setValues({
      ...form.values,
      servicePartner: servicePartner,
    });
    setSelectedServicePartner(servicePartner);

    // Call onChange if provided
    props.onChange?.(servicePartner);

    if (formStep) {
      formStep.next();
      if (scrollTo !== false) {
        scrollToElement({
          element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
        });
      }
    }
  };

  const toggleServicePartners = () => {
    setShowAllServicePartners(!showAllServicePartners);
  };

  // Determine which service partners to show
  const visibleServicePartners = () => {
    if (servicePartners.length === 0) return [];
    if (servicePartners.length === 1) return servicePartners;

    return showAllServicePartners ? servicePartners : [servicePartners[0]];
  };

  // Calculate how many partners are hidden
  const hiddenServicePartnersCount =
    servicePartners.length > 1 ? servicePartners.length - 1 : 0;

  return (
    <div className="formily-service-partner-selector">
      {loading ? (
        <div className="text-center p-4">
          <Loading />
        </div>
      ) : null}

      {error ? (
        <div className="text-center text-red-500 p-4">
          <p>
            Er is wat mis gegaan bij het zoeken naar herstellers. Controleer het
            ingevoerde adres en probeer het opnieuw.
          </p>
        </div>
      ) : null}

      {!loading && !error && servicePartners.length === 0 ? (
        <div className="text-center p-4">
          {props.contentOnInvalidServicepartner ? (
            <div
              dangerouslySetInnerHTML={{
                __html: props.contentOnInvalidServicepartner,
              }}
              className="text-red-500"
            />
          ) : (
            <p>Geen herstellers gevonden voor deze locatie.</p>
          )}
        </div>
      ) : null}

      {!loading && servicePartners.length > 0 ? (
        <div className="space-y-4">
          {visibleServicePartners().map((servicePartner) => (
            <ServicePartnerCard
              key={servicePartner["@id"]}
              servicePartner={servicePartner}
              isSelected={
                selectedServicePartner?.servicePartnerIri ===
                servicePartner.servicePartnerIri
              }
              onClick={handleSelectServicePartner}
              headerImageProps={props.headerImage}
              preferredPartnerText={props.preferredPartnerText}
            />
          ))}

          {hiddenServicePartnersCount > 0 ? (
            <div className="text-center">
              <Button
                type="default"
                className="font-bold tracking-normal"
                onClick={toggleServicePartners}
                icon={
                  showAllServicePartners ? <UpOutlined /> : <PlusOutlined />
                }
              >
                {showAllServicePartners
                  ? `${hiddenServicePartnersCount} vestigingen verbergen`
                  : `nog ${hiddenServicePartnersCount} weergeven`}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {props.errors && props.errors.length > 0 ? (
        <div className="text-red-500 p-4">
          {props.errors.map((error, index) => (
            <div key={index}>
              {props.contentOnInvalidServicepartner ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: props.contentOnInvalidServicepartner,
                  }}
                />
              ) : (
                error.messages.join(", ")
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const RumbleServicePartnerSelector = connect(
  ServicePartnerSelector,
  mapProps((props: any, field: any) => {
    return {
      ...props,
      value: props.value,
      onChange: props.onChange,
      invalid: field?.valid === false,
      errors: field?.errors,
    };
  }),
);

export default ServicePartnerSelector;
